import React from 'react';

interface FilterChipsProps {
  filters: {
    referralType?: string[];
    funder?: string[];
    bodyPart?: string[];
    providerOrg?: string[];
    imagingFacility?: string[];
    providerName?: string[];
    search?: string;
    assignee?: string; // AXIS148: Added assignee filter
    assigneeDisplayName?: string; // AXIS148: Display name for the assignee
    triageCategory?: number[]; // AXIS164: Triage category filter (1-4)
  };
  onRemoveFilter: (filterKey: string, value?: number | string) => void;
  onClearAll?: () => void;
}

const FILTER_LABELS: Record<string, string> = {
  referralType: 'Referral Type',
  funder: 'Funder',
  bodyPart: 'Body Part',
  providerOrg: 'Referrer Org',
  providerName: 'Referrer Name',
  search: 'Search',
  assignee: 'Assignee', // AXIS148
  triageCategory: 'Triage', // AXIS164 (per-chip label is "Triage: Category N")
};

export const FilterChips: React.FC<FilterChipsProps> = ({
  filters,
  onRemoveFilter,
  onClearAll,
}) => {
  // AXIS148: Exclude assigneeDisplayName from active filters count (it's just for display)
  // AXIS164: triageCategory is rendered as one chip per category
  const activeFilters = Object.entries(filters).filter(([key, value]) => {
    if (key === 'assigneeDisplayName') return false; // Skip display name - it's not a filter
    if (key === 'triageCategory') return Array.isArray(value) && value.length > 0;
    if (Array.isArray(value)) return value.length > 0;
    return value && typeof value === 'string' && value.trim();
  });

  if (activeFilters.length === 0) {
    return null;
  }

  const chips: { key: string; label: string; displayValue: string; onRemove: () => void }[] = [];
  activeFilters.forEach(([key, value]) => {
    if (key === 'triageCategory') {
      (value as number[]).forEach((cat: number) => {
        chips.push({
          key: `triageCategory-${cat}`,
          label: 'Triage',
          displayValue: `Category ${cat}`,
          onRemove: () => onRemoveFilter('triageCategory', cat),
        });
      });
      return;
    }
    let displayValue: string;
    if (key === 'assignee' && filters.assigneeDisplayName) {
      displayValue = filters.assigneeDisplayName;
    } else {
      displayValue = Array.isArray(value) ? value.join(', ') : value as string;
    }
    chips.push({
      key,
      label: FILTER_LABELS[key] || key,
      displayValue,
      onRemove: () => onRemoveFilter(key),
    });
  });

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 0',
        flexWrap: 'wrap',
      }}
    >
      {chips.map(({ key, label, displayValue, onRemove }) => (
        <div
          key={key}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: '#e8f4f8',
            border: '1px solid #b3dfe8',
            borderRadius: '16px',
            padding: '6px 12px',
            fontSize: '0.9em',
            color: '#0066a1',
          }}
        >
          <span style={{ fontWeight: 500 }}>{label}:</span>
          <span>{displayValue}</span>
          <button
            onClick={onRemove}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0 2px',
              color: '#0066a1',
              fontSize: '1.1em',
              display: 'flex',
              alignItems: 'center',
              marginLeft: '4px',
            }}
            title={`Remove ${label} ${displayValue} filter`}
          >
            ✕
          </button>
        </div>
      ))}

      {onClearAll && activeFilters.length > 0 && (
        <button
          onClick={onClearAll}
          style={{
            background: 'none',
            border: 'none',
            color: '#0066a1',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: '0.9em',
            padding: '6px 8px',
          }}
        >
          Clear All
        </button>
      )}
    </div>
  );
};
